import { useRecordContext, type RaRecord } from 'react-admin';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { SectionCard, fmtDate } from '../../shared/Panel';
import { useStatuses } from './statusDictionary';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  changeFieldLabel,
  changeValueText,
  knownVehicles,
  type ChangeFormatContext,
} from './changeHistoryFormat';
import { departmentName, hasLeadDepartment } from './processLead';
import { FIELD_RECOMMENDATION_SNAPSHOT, snapshotDetails } from './recommendationSnapshot';
import {
  FEATURE_ORDER_HISTORY_COLLAPSED,
  HISTORY_KINDS,
  HISTORY_KIND_LABELS,
  fetchOrderHistory,
  pageCount,
  readHistoryOpen,
  writeHistoryOpen,
  type HistoryKind,
  type HistoryPage,
  type HistorySummary,
} from './orderHistoryApi';

/** Pole dziennika ze zmianą stanu (klucz w bazie — dane). */
const FIELD_PLANNING_STATUS = 'planningStatus';

/**
 * Kto prowadzi zlecenie po zmianie stanu — dział stanu docelowego ze słownika
 * (praca dwutorowa, odpowiedź Marathonu 16.10.2026, pyt. 55). `null` — stan
 * bez działu (archiwum) albo spoza słownika: znacznika nie ma.
 */
export function leadAfterChange(
  newValue: unknown,
  states: ReadonlyArray<{ code: string; department?: string | null }>,
): string | null {
  const department = states.find((s) => s.code === newValue)?.department;
  return department ? `prowadzi: ${departmentName(department)}` : null;
}

/**
 * Historia zmian zlecenia (`UserOfferChange`).
 *
 * ## Dlaczego to dochodzi
 *
 * Każda operacja na zleceniu — zmiana stanu, kandydatura, obsadzenie, ryzyko,
 * termin — zapisuje wpis kto/kiedy/co/dlaczego. Do tej pory **nic tych wpisów
 * nie czytało**: audyt istniał wyłącznie w bazie. CLAUDE.md nazywa
 * audytowalność wymogiem przekrojowym, a harmonogram stawia „historię" w
 * pozycji 2 obok widoków FTL i Trade — bo przekazanie między działami bez
 * widocznej historii jest sporem o to, kto co zrobił.
 *
 * ## Co pokazujemy, a czego nie
 *
 * Pole, wartość przed i po, źródło (ręcznie / z TMS / system), autora
 * i powód. Wartości pokazujemy — to dane zlecenia, nie sekrety; bez nich wpis
 * nie odpowiedziałby na pytanie „co było wcześniej". Nazwy pól z dziennika
 * tłumaczymy tam, gdzie mamy pewność; nieznane pokazujemy jak są, zamiast
 * zgadywać. Daty, stany, tak/nie i pojazdy — po ludzku, jedną mapą
 * (`changeHistoryFormat.ts`).
 */

/**
 * Źródło wpisu przy autorze. Kody z bazy bez zmian; podpisy z poprawy
 * brzmienia 01.10.2026 — planista nie nazywa siebie „operatorem”, a „import”
 * i „reguła” to język techniczny.
 */
const SOURCE_LABELS: Record<string, string> = {
  MANUAL: 'ręcznie',
  // Import z TMS (`via/import-changes.ts`).
  IMPORT: 'z TMS',
  // Wpis dodany automatycznie, np. alert o przeplanowaniu (`replan-alerts.ts`).
  RULE: 'system',
  VIA: 'VIA',
  // Przypisanie zgłoszone przez OneHub (External Matching API v1.3).
  EXTERNAL: 'OneHub',
};

const display = (v: unknown) =>
  v === null || v === undefined || v === '' ? '—' : String(v);

/**
 * Klucz wiersza: id wpisu, a bez niego — miejsce na liście. Wpis bez id
 * przychodzi z listy zleceń: react-admin podstawia wiersz listy jako rekord
 * karty, zanim przyjdą szczegóły, a lista niesie z dziennika tylko datę
 * ostatniego wpisu (`LIST_INCLUDE` w API). Bez klucza React ostrzegał
 * w konsoli przy każdym wejściu z listy w kartę (e2e, 06.10.2026).
 */
const changeRowKey = (w: RaRecord, index: number): string =>
  w.id === undefined || w.id === null ? `row-${index}` : String(w.id);

/**
 * Szczegóły migawki rekomendacji z chwili decyzji (20.10.2026) — pierwsze
 * pozycje listy, werdykty i uwagi auta; zwinięte, żeby historia została
 * tabelą wpisów.
 */
const SnapshotDetails = ({ value }: { value: unknown }) => {
  const [open, setOpen] = useState(false);
  const lines = snapshotDetails(value);
  if (lines.length === 0) return null;
  return (
    <>
      <Button size="small" sx={{ ml: 0.5, py: 0 }} onClick={() => setOpen((o) => !o)}>
        {open ? 'Zwiń' : 'Szczegóły'}
      </Button>
      {open ? (
        <Stack component="ul" sx={{ m: 0, pl: 2 }} data-testid="snapshot-details">
          {lines.map((line) => (
            <Typography key={line} component="li" variant="caption" color="text.secondary">
              {line}
            </Typography>
          ))}
        </Stack>
      ) : null}
    </>
  );
};

/** Tabela wpisów — wspólna dla karty jak dotąd i dla historii stronicowanej. */
const ChangeEntriesTable = ({
  entries,
  ctx,
  showLead,
  states,
}: {
  entries: RaRecord[];
  ctx: ChangeFormatContext;
  showLead: boolean;
  states: ReturnType<typeof useStatuses>;
}) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell sx={{ color: 'text.secondary' }}>Kiedy</TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>Co</TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>Przed</TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>Po</TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>Kto</TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>Powód</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {entries.map((w, index) => {
        const field = String(w.field ?? '');
        const source = w.source ? String(w.source) : null;
        const author = (w.changedBy as RaRecord | undefined)?.username;
        return (
          <TableRow key={changeRowKey(w, index)} data-testid="order-history-row">
            <TableCell sx={{ border: 0, whiteSpace: 'nowrap' }}>
              {fmtDate(w.changedAt as string)}
            </TableCell>
            <TableCell sx={{ border: 0 }}>
              {changeFieldLabel(field)}
            </TableCell>
            <TableCell sx={{ border: 0, color: 'text.secondary' }}>
              {changeValueText(field, w.oldValue, ctx)}
            </TableCell>
            <TableCell sx={{ border: 0 }}>
              {changeValueText(field, w.newValue, ctx)}
              {field === FIELD_RECOMMENDATION_SNAPSHOT ? <SnapshotDetails value={w.newValue} /> : null}
              {showLead && field === FIELD_PLANNING_STATUS && leadAfterChange(w.newValue, states) ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={leadAfterChange(w.newValue, states)}
                  sx={{ ml: 0.5 }}
                  data-testid="history-lead"
                />
              ) : null}
            </TableCell>
            <TableCell sx={{ border: 0 }}>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <span>{author ? String(author) : '—'}</span>
                {source ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={SOURCE_LABELS[source] ?? source}
                  />
                ) : null}
              </Stack>
            </TableCell>
            <TableCell sx={{ border: 0, color: 'text.secondary' }}>
              {display(w.reason)}
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
);

const EMPTY_TEXT =
  'Brak wpisów — zlecenie nie było jeszcze zmieniane ani ręcznie, ani automatycznie.';

/**
 * Historia zmian na karcie zlecenia. Z przełącznikiem `orderHistoryCollapsed`
 * (24.10.2026, S-24.10-02) — zwinięta pod metryką, pełna ze stronicowaniem
 * i filtrem (`PagedChangeHistory`); bez niego jak dotąd: 100 najnowszych
 * wpisów ze szczegółów zlecenia.
 */
export const ChangeHistory = () =>
  useFeature(FEATURE_ORDER_HISTORY_COLLAPSED) ? <PagedChangeHistory /> : <RecentChangeHistory />;

/** Historia jak dotąd — wpisy ze szczegółów zlecenia (najwyżej 100). */
const RecentChangeHistory = () => {
  const r = useRecordContext();
  const states = useStatuses();
  const entries = (r?.changes as RaRecord[] | undefined) ?? [];
  // „Prowadzi” przy zmianach stanu — tylko gdy API niesie `leadDepartment`,
  // czyli z przełącznikiem `tradeDualTrack` (odpowiedzi 16.10.2026).
  const showLead = hasLeadDepartment(r as Record<string, unknown> | undefined);
  // Etykiety pól i zapis wartości: `changeHistoryFormat.ts`.
  // „Zakończona” zamiast „załatwiona” (21.10.2026, `ftlDecisionNotifications`).
  const ftlNoteEnded = useFeature('ftlDecisionNotifications');
  const ctx: ChangeFormatContext = {
    states,
    vehicles: knownVehicles(r as Record<string, unknown> | undefined),
    ftlNoteEnded,
  };

  return (
    <SectionCard title={`Historia zmian (${entries.length})`}>
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {EMPTY_TEXT}
        </Typography>
      ) : (
        <ChangeEntriesTable entries={entries} ctx={ctx} showLead={showLead} states={states} />
      )}
    </SectionCard>
  );
};

/** Metryka zwiniętej historii: kiedy, kto, co — ostatnia zmiana. */
export function lastChangeText(summary: HistorySummary): string {
  if (summary.last === null) return EMPTY_TEXT;
  const who = summary.last.author ?? SOURCE_LABELS[summary.last.source ?? ''] ?? '—';
  return `Ostatnia zmiana: ${fmtDate(summary.last.changedAt)} · ${who} · ${changeFieldLabel(summary.last.field)}`;
}

/**
 * Historia zwinięta pod metryką (decyzja z paczki p. Mariusza 20.10.2026,
 * MIX-01; model rekordu §7.4 „Historia (zwinięta)”). Metryka: liczba wpisów,
 * ostatnia zmiana (kiedy, kto, co) i liczba decyzji ludzi. Rozwinięta —
 * cała historia strona po stronie (`GET /user-offers/:id/changes`) z filtrem
 * rodzaju; wybór „rozwinięta” pamięta przeglądarka. Wpisy alertów, których
 * konto nie widzi, odsiewa API.
 *
 * Odświeża się, gdy karta dostanie nowe szczegóły z nowym wpisem (po każdej
 * operacji react-admin pobiera zlecenie od nowa).
 */
const PagedChangeHistory = () => {
  const r = useRecordContext();
  const states = useStatuses();
  const ftlNoteEnded = useFeature('ftlDecisionNotifications');
  const showLead = hasLeadDepartment(r as Record<string, unknown> | undefined);
  const [open, setOpen] = useState<boolean>(() => readHistoryOpen());
  const [kind, setKind] = useState<HistoryKind>('all');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<HistoryPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const id = r?.id as number | string | undefined;
  // Najnowszy wpis ze szczegółów — zmienia się po każdej operacji na zleceniu.
  const newest = ((r?.changes as RaRecord[] | undefined) ?? [])[0]?.id ?? null;

  // Inne zlecenie (nawigacja między kartami bez przeładowania) — od pierwszej strony, bez wpisów poprzedniego.
  useEffect(() => {
    setResult(null);
    setKind('all');
    setPage(1);
  }, [id]);

  useEffect(() => {
    if (id === undefined || id === null) return undefined;
    let alive = true;
    setError(null);
    fetchOrderHistory(id, { kind, page })
      .then((p) => {
        if (alive) setResult(p);
      })
      .catch(() => {
        if (alive) setError('Nie udało się wczytać historii zmian — odśwież kartę.');
      });
    return () => {
      alive = false;
    };
  }, [id, kind, page, newest]);

  const toggle = () => {
    writeHistoryOpen(!open);
    setOpen(!open);
  };
  const chooseKind = (k: HistoryKind) => {
    setKind(k);
    setPage(1);
  };

  const summary = result?.summary ?? null;
  const vehicles = knownVehicles({
    ...((r as Record<string, unknown> | undefined) ?? {}),
    vehicleNumbers: {
      ...((r?.vehicleNumbers as Record<string, string> | undefined) ?? {}),
      ...(result?.vehicleNumbers ?? {}),
    },
  });
  const ctx: ChangeFormatContext = { states, vehicles, ftlNoteEnded };
  const pages = result ? pageCount(result.total, result.perPage) : 1;

  return (
    <Box data-testid="order-history">
      <SectionCard title={`Historia zmian (${summary ? summary.total : '…'})`}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          data-testid="order-history-metric"
        >
          <Typography variant="body2" sx={{ flex: '1 1 320px', minWidth: 0 }}>
            {summary === null ? (error ?? 'Wczytuję historię…') : lastChangeText(summary)}
          </Typography>
          {summary ? (
            <Chip
              size="small"
              variant="outlined"
              label={`Decyzje ludzi: ${summary.humanDecisions}`}
              data-testid="order-history-human"
            />
          ) : null}
          <Button size="small" onClick={toggle} data-testid="order-history-toggle" aria-expanded={open}>
            {open ? 'Zwiń' : 'Rozwiń historię'}
          </Button>
        </Stack>
        {open ? (
          <Box sx={{ mt: 1 }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={kind}
              onChange={(_e, v: HistoryKind | null) => {
                if (v) chooseKind(v);
              }}
              aria-label="Rodzaj wpisów"
              sx={{ mb: 1, flexWrap: 'wrap' }}
            >
              {HISTORY_KINDS.map((k) => (
                <ToggleButton key={k} value={k} data-testid={`order-history-filter-${k}`}>
                  {HISTORY_KIND_LABELS[k]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {error && result !== null ? (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            ) : null}
            {result && result.data.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {kind === 'all' ? EMPTY_TEXT : `Brak wpisów w rodzaju „${HISTORY_KIND_LABELS[kind]}”.`}
              </Typography>
            ) : null}
            {result && result.data.length > 0 ? (
              <ChangeEntriesTable
                entries={result.data as unknown as RaRecord[]}
                ctx={ctx}
                showLead={showLead}
                states={states}
              />
            ) : null}
            {result && result.total > result.perPage ? (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1 }}>
                <Button
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="order-history-prev"
                >
                  Nowsze
                </Button>
                <Typography variant="caption" color="text.secondary" data-testid="order-history-page">
                  Strona {page} z {pages} · wpisów: {result.total}
                </Typography>
                <Button
                  size="small"
                  disabled={page >= pages}
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  data-testid="order-history-next"
                >
                  Starsze
                </Button>
              </Stack>
            ) : null}
          </Box>
        ) : null}
      </SectionCard>
    </Box>
  );
};
